import { getDatabase, ref, remove, update, push, set } from "firebase/database";
import FBLearnmoreBuilder from "../models/builders/FBLearnmoreBuilder";
import FBRightsCategoryBuilder from "../models/builders/FBRightsCategoryBuilder";
import FBSubrightBuilder from "../models/builders/FBSubrightBuilder";
import * as FirebaseGet from "./FirebaseGet";

const kRightsCategory = "rights-categories";
const kSubrights = "subrights";
const kTranslation = "translation";
const kLearnmores = "learn-mores";
const kEnglish = "en";

const pushNewTranslationNew = (hash, language, text) => {
  return new Promise((resolve, reject) => {
    const translationRef = ref(
      getDatabase(),
      `${kTranslation}/${hash}/${language}`
    );
    set(translationRef, text)
      .then(() => {
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const pushInfoChunks = (hashedInfoChunks, infoChunks) => {
  const promises = [];

  hashedInfoChunks.forEach((infoChunk, index) => {
    const bodyHash = infoChunk.body;
    const headerHash = infoChunk.header;
    const bodyString = infoChunks[index].body;
    const headerString = infoChunks[index].header;
    promises.push(pushNewTranslationNew(bodyHash, kEnglish, bodyString));
    promises.push(pushNewTranslationNew(headerHash, kEnglish, headerString));
  });

  return promises;
};

const deleteHashedInfoChunkText = (hashedInfoChunks) => {
  const promises = [];

  hashedInfoChunks.forEach((infoChunk) => {
    const bodyHash = infoChunk.body;
    const headerHash = infoChunk.header;
    promises.push(remove(ref(getDatabase(), `${kTranslation}/${bodyHash}`)));
    promises.push(remove(ref(getDatabase(), `${kTranslation}/${headerHash}`)));
  });

  return promises;
};

const pushNewRightCategoryNew = (fbRightsCategory) => {
  return new Promise((resolve, reject) => {
    if (fbRightsCategory == null) {
      reject("Information was null");
    }

    const promises = [];
    promises.push(
      pushNewTranslationNew(
        fbRightsCategory.titleHash,
        kEnglish,
        fbRightsCategory.title
      )
    );
    promises.push(
      pushNewTranslationNew(
        fbRightsCategory.subtitleHash,
        kEnglish,
        fbRightsCategory.subtitle
      )
    );
    promises.push(
      pushNewTranslationNew(
        fbRightsCategory.descriptionHash,
        kEnglish,
        fbRightsCategory.description
      )
    );

    Promise.all(promises)
      .then(() => {
        const newRightRef = ref(getDatabase(), kRightsCategory);
        const jsonToPush =
          FBRightsCategoryBuilder.buildJSONToPush(fbRightsCategory);
        push(newRightRef, jsonToPush)
          .then(() => {
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const pushNewSubrightNew = (fbSubright) => {
  return new Promise((resolve, reject) => {
    if (fbSubright == null) {
      reject("Information was null");
    }
    const promises = [];
    const subrightObj = FBSubrightBuilder.buildJSONToPush(fbSubright);

    promises.push(
      pushNewTranslationNew(fbSubright.titleHash, kEnglish, fbSubright.title)
    );
    promises.push(
      pushNewTranslationNew(
        fbSubright.descriptionHash,
        kEnglish,
        fbSubright.description
      )
    );
    promises.push(push(ref(getDatabase(), `${kSubrights}`), subrightObj));
    Promise.all(promises)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const pushNewLearnMoreNew = (fbLearnmore) => {
  return new Promise((resolve, reject) => {
    if (fbLearnmore == null) {
      reject("Information was null");
    }
    const chunkPromises = pushInfoChunks(
      fbLearnmore.hashedInfoChunks,
      fbLearnmore.informationChunks
    );
    const promises = [...chunkPromises];
    const learnMoreObj = FBLearnmoreBuilder.buildJSONToPush(fbLearnmore);
    promises.push(
      pushNewTranslationNew(fbLearnmore.titleHash, kEnglish, fbLearnmore.title)
    );
    promises.push(push(ref(getDatabase(), `${kLearnmores}`), learnMoreObj));

    Promise.all(promises)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const editRightCategoryNew = (fbNewRightsCategory, oldCategory) => {
  return new Promise((resolve, reject) => {
    if (fbNewRightsCategory == null) {
      reject("Information was null");
    }
    const updates = {};
    const translationPromises = [];
    if (fbNewRightsCategory.title !== oldCategory.title) {
      const titleHash = fbNewRightsCategory.titleHash;
      updates["title"] = titleHash;
      translationPromises.push(
        pushNewTranslationNew(titleHash, kEnglish, fbNewRightsCategory.title)
      );
      const translationRef = ref(
        getDatabase(),
        `${kTranslation}/${oldCategory.titleHash}`
      );
      translationPromises.push(remove(translationRef));
    }
    if (oldCategory.subtitle !== fbNewRightsCategory.subtitle) {
      const subtitleHash = fbNewRightsCategory.subtitleHash;
      updates["subtitle"] = subtitleHash;
      translationPromises.push(
        pushNewTranslationNew(
          subtitleHash,
          kEnglish,
          fbNewRightsCategory.subtitle
        )
      );
      const translationRef = ref(
        getDatabase(),
        `${kTranslation}/${oldCategory.subtitleHash}`
      );
      translationPromises.push(remove(translationRef));
    }
    if (oldCategory.description !== fbNewRightsCategory.description) {
      const descriptionHash = fbNewRightsCategory.descriptionHash;
      updates["description"] = descriptionHash;
      translationPromises.push(
        pushNewTranslationNew(
          descriptionHash,
          kEnglish,
          fbNewRightsCategory.description
        )
      );
      const translationRef = ref(
        getDatabase(),
        `${kTranslation}/${oldCategory.descriptionHash}`
      );
      translationPromises.push(remove(translationRef));
    }

    Promise.all(translationPromises)
      .then(() => {
        const updateRef = ref(
          getDatabase(),
          `${kRightsCategory}/${oldCategory.id}`
        );
        update(updateRef, updates)
          .then(() => {
            resolve();
          })
          .catch((err) => {
            reject(err);
          });
      })
      .catch(() => {
        reject();
      });
  });
};

const editSubrightNew = (fbNewSubright, oldSubright) => {
  return new Promise((resolve, reject) => {
    if (fbNewSubright == null) {
      reject("Information was null");
    }
    const updates = {};
    const promises = [];
    if (oldSubright.title !== fbNewSubright.title) {
      const titleHash = fbNewSubright.titleHash;
      updates["title"] = titleHash;
      promises.push(
        pushNewTranslationNew(titleHash, kEnglish, fbNewSubright.title)
      );
      promises.push(
        remove(ref(getDatabase(), `${kTranslation}/${oldSubright.titleHash}`))
      );
    }
    if (oldSubright.description !== fbNewSubright.description) {
      const descriptionHash = fbNewSubright.descriptionHash;
      updates["description"] = descriptionHash;
      promises.push(
        pushNewTranslationNew(
          descriptionHash,
          kEnglish,
          fbNewSubright.description
        )
      );
      promises.push(
        remove(
          ref(getDatabase(), `${kTranslation}/${oldSubright.descriptionHash}`)
        )
      );
    }
    updates["organizations"] = fbNewSubright.organizations;
    promises.push(
      update(ref(getDatabase(), `${kSubrights}/${oldSubright.id}`), updates)
    );
    Promise.all(promises)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const editLearnMoreNew = (fbEditLearnmore, oldLearnmore) => {
  return new Promise((resolve, reject) => {
    if (fbEditLearnmore == null) {
      reject("Information was null");
    }
    const deletedHashesPromises = deleteHashedInfoChunkText(
      oldLearnmore.hashedInfoChunks
    );
    Promise.all(deletedHashesPromises)
      .then(() => {
        const promises = pushInfoChunks(
          fbEditLearnmore.hashedInfoChunks,
          fbEditLearnmore.informationChunks
        );
        const newHashedInfoChunks = fbEditLearnmore.hashedInfoChunks;
        const newHashedPromises = promises;
        Promise.all(newHashedPromises)
          .then(() => {
            const updates = {};
            const promises = [];
            updates["informationChunks"] = newHashedInfoChunks;
            if (oldLearnmore.title !== fbEditLearnmore.title) {
              const titleHash = fbEditLearnmore.titleHash;
              updates["title"] = titleHash;
              promises.push(
                pushNewTranslationNew(
                  titleHash,
                  kEnglish,
                  fbEditLearnmore.title
                )
              );
              promises.push(
                remove(
                  ref(
                    getDatabase(),
                    `${kTranslation}/${oldLearnmore.titleHash}`
                  )
                )
              );
            }
            promises.push(
              update(
                ref(getDatabase(), `${kLearnmores}/${oldLearnmore.id}`),
                updates
              )
            );
            Promise.all(promises)
              .then(() => {
                console.log("SUCCESS");
                resolve();
              })
              .catch((error) => {
                console.log(error);
                reject(error);
              });
          })
          .catch((error) => {
            console.log(error);
            reject(error);
          });
      })
      .catch((error) => {
        console.log(error);
        reject(error);
      });
  });
};

const removeRightCategoryNew = (rightCategory) => {
  return new Promise((resolve, reject) => {
    FirebaseGet.fetchSubrightsNew(rightCategory.id)
      .then((subrights) => {
        const promises = [];
        subrights.forEach((subright) => {
          promises.push(removeSubrightNew(subright));
        });
        promises.push(
          remove(
            ref(getDatabase(), `${kTranslation}/${rightCategory.titleHash}`)
          )
        );
        promises.push(
          remove(
            ref(
              getDatabase(),
              `${kTranslation}/${rightCategory.descriptionHash}`
            )
          )
        );
        promises.push(
          remove(
            ref(getDatabase(), `${kTranslation}/${rightCategory.subtitleHash}`)
          )
        );
        promises.push(
          remove(ref(getDatabase(), `${kRightsCategory}/${rightCategory.id}`))
        );
        Promise.all(promises)
          .then(() => {
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const removeSubrightNew = (subright) => {
  return new Promise((resolve, reject) => {
    FirebaseGet.fetchLearnMoresNew(subright.id)
      .then((learnMores) => {
        const promises = [];
        learnMores.forEach((learnMore) => {
          promises.push(removeLearnmoreNew(learnMore, subright));
        });
        promises.push(
          remove(ref(getDatabase(), `${kTranslation}/${subright.titleHash}`))
        );
        promises.push(
          remove(
            ref(getDatabase(), `${kTranslation}/${subright.descriptionHash}`)
          )
        );
        promises.push(
          remove(ref(getDatabase(), `${kSubrights}/${subright.id}`))
        );
        Promise.all(promises)
          .then(() => {
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const removeLearnmoreNew = (learnMore, parentSubright) => {
  return new Promise((resolve, reject) => {
    const promises = [];
    learnMore.hashedInfoChunks.forEach((chunk) => {
      promises.push(
        remove(ref(getDatabase(), `${kTranslation}/${chunk.body}`))
      );
      promises.push(
        remove(ref(getDatabase(), `${kTranslation}/${chunk.header}`))
      );
    });
    promises.push(
      remove(ref(getDatabase(), `${kTranslation}/${learnMore.titleHash}`))
    );
    promises.push(remove(ref(getDatabase(), `${kLearnmores}/${learnMore.id}`)));
    const updatedLearnMoreArr = parentSubright.learnMores.filter(
      (learnMoreID) => learnMoreID !== learnMore.id
    );
    promises.push(
      update(ref(getDatabase(), `${kSubrights}/${parentSubright.id}`), {
        learnMores: updatedLearnMoreArr,
      })
    );

    Promise.all(promises)
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export {
  pushNewRightCategoryNew,
  editRightCategoryNew,
  removeRightCategoryNew,
  pushNewSubrightNew,
  editSubrightNew,
  removeSubrightNew,
  pushNewLearnMoreNew,
  editLearnMoreNew,
  removeLearnmoreNew,
};
